import { useLocation } from "react-router-dom";

import error4xxImage from "assets/images/404.png";
import error5xxImage from "assets/images/500.png";

import { useAppSelector } from "store/hooks";

import Button from "components/Button";

import { UserRole } from "entities/common";
import PageTemplate from "modules/common/PageTemplate";
import { PAGES } from "utils/links";

import styles from "./style.module.scss";

const ERROR_MESSAGES: Record<number, string> = {
  400: "Bad request. The server could not process your request.",
  401: "Your session has expired. Please log in again.",
  403: "Access is limited. Unfortunately, you do not have permission to view this section.",
  404: "Oops! This page doesn't exist. Check the address or return to the main page.",
  409: "A conflict occurred. The resource may have been modified by another user.",
  410: "This resource is no longer available.",
  429: "Too many requests. Please wait a moment and try again.",
  500: "Something went wrong. Please try refreshing the page later.",
  502: "Something went wrong. Please try refreshing the page later.",
  503: "Something went wrong. Please try refreshing the page later.",
  504: "Something went wrong. Please try refreshing the page later.",
};

const DEFAULT_4XX_MESSAGE = "Oops! This page doesn't exist. Check the address or return to the main page.";
const DEFAULT_5XX_MESSAGE = "Something went wrong. Please try refreshing the page later.";

const ErrorPage = () => {
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);

  const pathCode = parseInt(location.pathname.replace("/", ""), 10);
  const errorCode = !isNaN(pathCode) ? pathCode : 404;
  const is5xx = errorCode >= 500 && errorCode < 600;
  const defaultMessage = is5xx ? DEFAULT_5XX_MESSAGE : DEFAULT_4XX_MESSAGE;
  const message = ERROR_MESSAGES[errorCode] || defaultMessage;
  const image = is5xx ? error5xxImage : error4xxImage;

  const mainPageLink = user?.role === UserRole.ADMINISTRATOR ? PAGES.COMPANY_LIST : PAGES.PORTFOLIOS_LIST;

  return (
    <PageTemplate className={styles.errorPage}>
      <div className={styles.content}>
        <img src={image} alt={`Error ${errorCode}`} className={styles.image} />

        <div className={styles.textBlock}>
          <h1 className={styles.title}>Error {errorCode}</h1>
          <p className={styles.message}>{message}</p>
        </div>

        <a href={mainPageLink}>
          <Button variant="secondary" className={styles.button}>
            Main page
          </Button>
        </a>
      </div>
    </PageTemplate>
  );
};

export default ErrorPage;
